import { Injectable } from '@angular/core'
import { ApiService } from '@app/core/graphql/services/api.service'
import { AuthService } from '@app/core/auth/services/auth.service'
import { BranchWithStatus, CreateBranchInput, UpdateBranchInput } from '@app/models/branch.model'
import { UpdateEventInput } from '@app/models/event.model'
import { EventUpdateLocation } from '@app/models/location.model'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { GET_BRANCH, SET_BRANCH, UPDATE_BRANCH } from '@app/query/branch.query'
import { UPDATE_EVENT } from '@app/query/event.query'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { serializeAs } from '@app/utils/serialize'
import { pick } from 'lodash-es'
import { MessageService } from 'primeng/api'
import { map, Subject, tap } from 'rxjs'
import { PassUploadJobsService } from '@app/shared/services/pass-upload-jobs.service'

@Injectable({
  providedIn: 'root',
})
export class BranchService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'BRANCH'
  sysMessage: any

  /** Backing store for `branches` — access only through the getter/setter below. */
  private _branches: Partial<BranchWithStatus>[] = []

  /** The session identity (getIdentityKey — cid:uid claims, NOT the raw JWT, which PAPI re-signs
   *  on every response) `branches` was populated under. Dashboards assign this field after their
   *  own get_filtered_events, but label callbacks (branch()/areasArray()) read it lazily, and
   *  logout/login is an SPA navigation that keeps this root service alive — so without this stamp
   *  a client switch would resolve rows against the previous client's events. Event ids are
   *  per-tenant sequences (not globally unique), so a colliding id would render the wrong client's
   *  event name/areas rather than just missing. */
  private branchesIdentity: string | null = null

  /** Branches array of objects, valid only for the session identity it was fetched under. */
  get branches(): Partial<BranchWithStatus>[] {
    const identity = this.authService.getIdentityKey()
    if (identity !== this.branchesIdentity) {
      this.branchesIdentity = identity
      this._branches = []
    }
    return this._branches
  }

  set branches(value: Partial<BranchWithStatus>[]) {
    this.branchesIdentity = this.authService.getIdentityKey()
    this._branches = value
  }

  constructor(
    private API: ApiService,
    private translateMessagesService: TranslateMessagesService,
    private messageService: MessageService,
    private authService: AuthService,
    private passUploadJobs: PassUploadJobsService,
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  createNew(payload: Partial<any>, silence?: boolean) {
    return this.API.privateMutation<any>(SET_BRANCH, (data: any) => {
      return {
        data: serializeAs(CreateBranchInput, pick(Object.assign(payload, data), Object.keys(new CreateBranchInput()))) as CreateBranchInput,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('CREATE_SUCCESS', payload),
          })
          this.subscriber.next({
            type: `SET_${this.id}`,
            data: payload,
          })
        }
      }),
    )
  }

  update(payload: any, isInstitute: boolean = false) {
    if (payload.update_location) {
      if ('object' == typeof payload.update_location.name) {
        payload.update_location.name = payload.update_location.name?.name || null
      }
      if (!payload.update_location.lat || !payload.update_location.lon) {
        payload.update_location = null
      } else {
        payload.update_location = serializeAs(EventUpdateLocation, pick(payload.update_location, Object.keys(new EventUpdateLocation())))
      }
    }
    return this.API.privateMutation<any>(!isInstitute ? UPDATE_EVENT : UPDATE_BRANCH, (data: any) => {
      const merged = Object.assign(payload, data)

      const keys = Object.keys(!isInstitute ? new UpdateEventInput() : new UpdateBranchInput())
      if (!keys.includes('secured')) {
        keys.push('secured')
      }

      const obj = pick(merged, keys)

      return {
        data: serializeAs<UpdateEventInput | UpdateBranchInput>(!isInstitute ? UpdateEventInput : UpdateBranchInput, obj),
      }
    }).pipe(
      tap((data: any) => {
        /* YB-1669: a tickets_url excel was staged + auto-queued — track it in the bell panel. */
        if (data?.success && data?.data?.pass_upload_job_id) this.passUploadJobs.trackJob(+data.data.pass_upload_job_id)
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data.location as EventUpdateLocation),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data as EventUpdateLocation,
          })
        }
      }),
    )
  }

  getBranch(branch_id: string) {
    return this.API.privateQuery<any>(GET_BRANCH, (data: any) => {
      data.id = parseInt(branch_id)
      return data
    }).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data as BranchWithStatus
        } else {
          return {} as BranchWithStatus
        }
      }),
    )
  }

  /**
   * Get selected areas array of object by provide array of ares ids
   * @param model Model data
   * @param selectedBranch Selected branch search
   * @param specificKey Specific key for search in selectedBranch
   */
  getAreasArrayByIdsArray(model: any, selectedBranch: any, specificKey?: string) {
    const areas: any[] = []

    // Normalize areas to array - handle both string (e.g., "1,2,3") and array formats
    let areasArray: string[] = []
    if (model.areas) {
      if (typeof model.areas === 'string') {
        // Split string by comma and trim whitespace
        areasArray = model.areas.split(',').map((id: string) => id.trim())
      } else if (Array.isArray(model.areas)) {
        areasArray = model.areas.map((id: any) => id.toString().trim())
      }
    }

    if (!specificKey) {
      selectedBranch?.entrances?.forEach((entrance: any) => {
        entrance?.areas?.forEach((area: any) => {
          if (areasArray.includes(area.id.toString())) {
            areas.push(area)
          }
        })
      })
    } else {
      selectedBranch[specificKey]?.forEach((area: any) => {
        if (areasArray.includes(area.id.toString())) {
          areas.push(area)
        }
      })
    }
    return areas
  }

  /**
   * Get areas array by invitation event_id
   * @param pass Invitation
   * @returns areas array
   */
  areasArray(pass: any) {
    const branch = this.branch(pass.event_id)
    return this.getAreasArrayByIdsArray(pass, branch)
  }

  /**
   * Get branch  by event id
   * @param event_id Event id
   * @returns Branch
   */
  branch(event_id: any) {
    return this.branches?.find((branch: any) => branch?.id?.toString() == event_id?.toString())
  }

  branchLabelCallback = (child: any) => {
    return this.branch(child.event_id)?.name
  }
}
